import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../../images/laundry-care-logo.png';
import { API } from '../../api/axios';

function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [errors, setErrors] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const isMounted = React.useRef(true);

  React.useEffect(() => {
    // Clear auth data
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');

    // Clear any cached requests
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }

    // Clear session storage as well
    sessionStorage.clear();

    return () => {
      isMounted.current = false;
    };
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/^[\w.+-]+@([\w-]+\.)+[\w-]{2,4}$/.test(email)) {
      newErrors.email = 'Invalid email address';
    }
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    if (isMounted.current) {
      setErrors(newErrors);
    }
    return Object.keys(newErrors).length === 0;
  };

  const loginAsAdmin = async (email, password) => {
    if (!validate()) return;

    setLoading(true);
    try {
      // Request with populate to get role information
      const res = await API.post('/auth/local?populate=role', {
        identifier: email,
        password,
      });

      console.log('✅ Login response:', res.data); // Debug log

      if (res.status === 200 && res.data.jwt) {
        let user = res.data.user;

        // If role is not populated, fetch user details with role
        if (!user.role) {
          console.log('🔄 Role not populated, fetching user details...');
          try {
            const userDetailRes = await API.get(
              `/users/${user.id}?populate=role`,
              {
                headers: {
                  Authorization: `Bearer ${res.data.jwt}`,
                },
              }
            );
            user = userDetailRes.data;
            console.log('✅ User details with role:', user);
          } catch (err) {
            console.error('Failed to fetch user details:', err);
          }
        }

        // Strapi Users & Permissions plugin returns role as an object with name property
        const roleObj = user?.role;
        const roleName = (roleObj?.name || roleObj?.type || roleObj || '')
          .toString()
          .toLowerCase();

        console.log('👤 User:', user.username || user.email);
        console.log('🔐 Role object:', roleObj);
        console.log('📋 Role name:', roleName);

        // Check if user has admin or super admin role
        // Check for: "Admin", "SuperAdmin", "admin", "superadmin", or any role containing "admin"
        const isAdmin =
          roleName.includes('admin') ||
          roleName.includes('super') ||
          roleObj?.name?.toLowerCase().includes('admin') ||
          roleObj?.type?.toLowerCase().includes('admin');

        console.log('✅ Is Admin?', isAdmin);

        if (isAdmin) {
          localStorage.setItem('adminToken', res.data.jwt);
          localStorage.setItem('adminUser', JSON.stringify(user));
          console.log('🎉 Admin login successful, redirecting...');
          navigate('/admin/dashboard');
        } else {
          console.error('❌ User role not authorized:', roleObj);
          console.error(
            '💡 Expected role name to contain "admin", but got:',
            roleName
          );
          alert(
            `Access denied. Admin privileges required.\n\nYour role: ${
              roleObj?.name || 'Unknown'
            }\n\nPlease contact an administrator.`
          );
          if (isMounted.current) {
            setErrors({ email: 'You do not have admin access' });
          }
        }
      } else {
        alert('Login failed or token missing');
      }
    } catch (err) {
      console.error('Admin login error:', err);
      console.error('Error details:', err.response?.data);
      alert('Login failed. Please check your credentials.');
      if (isMounted.current) {
        setErrors({ email: 'Invalid credentials or insufficient permissions' });
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    loginAsAdmin(email, password);
  };

  return (
    <main className="bg-white">
      <div className="flex items-center justify-center min-h-screen bg-blue-50">
        <div className="flex flex-col after:flex-1">
          <div className="flex items-center justify-center h-16 px-4 mb-4 sm:mb-20 sm:px-6 lg:px-8">
            <Link className="block" to="/">
              <img className="w-80" src={logo} alt="Laundry Care" />
            </Link>
          </div>
          <div className="min-w-max align-middle">
            <div className="text-center">
              <p className="text-blue-500 text-2xl font-semibold my-1">
                Admin Portal
              </p>
              <p className="text-portal-400 mb-5">
                Sign in to access the admin dashboard
              </p>
            </div>
            <div className="px-8 sm:px-10 py-8 sm:py-10 mx-5 sm:mx-1 shadow-2xl border border-slate-200 rounded-lg bg-white">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    className="block font-medium mb-1"
                    htmlFor="admin-email">
                    Email
                  </label>
                  <input
                    id="admin-email"
                    className="form-input w-full bg-slate-50 border-0 rounded-lg"
                    type="email"
                    autoComplete="on"
                    onChange={(e) => setEmail(e.target.value)}
                    value={email}
                    required
                    autoFocus
                    placeholder="admin@laundryCare.com"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>
                <div>
                  <label
                    className="block font-medium mb-1"
                    htmlFor="admin-password">
                    Password
                  </label>
                  <input
                    id="admin-password"
                    className="form-input w-full bg-slate-50 border-0 rounded-lg"
                    type="password"
                    onChange={(e) => setPassword(e.target.value)}
                    value={password}
                    required
                    placeholder="********"
                    autoComplete="on"
                  />
                  {errors.password && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.password}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap mt-5">
                  <div className="w-full">
                    <button
                      className="btn py-2.5 text-md font-semibold text-white bg-blue-500 hover:bg-blue-700 w-full rounded-full disabled:opacity-50"
                      type="submit"
                      disabled={loading}>
                      {loading ? 'Signing in...' : 'Sign In as Admin'}
                    </button>
                  </div>
                </div>
              </form>
              <div className="pt-5 mt-6 flex justify-center border-t border-slate-200">
                <div className="text-sm text-gray-600">
                  <Link
                    className="font-medium text-blue-500 hover:text-blue-800"
                    to="/signin">
                    ← Back to Applicant Login
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default AdminLogin;
